/**
 * @file UsersViewProvider.tsx
 * @description
 * -----------------------------------------------------
 * Proveedor de contexto para permisos y configuración de la vista
 * de usuarios. Simplificado para super-admin (sin lógica de roles)
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-12-11
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import ErrorView from "@/components/ui/ErrorView";
import UsersSkeleton from "@/components/ui/skeletons/super-admin/UsersSkeleton";
import { createContext, PropsWithChildren, Suspense, use } from "react";
import { TablePages } from "@/enums/tablePages.enum";
import { ColumnsAllUsers } from "@/types/commons/columns";
import { ActionConfig, BaseSelectionActionPermissions } from "@/types/actions";
import { UsersViewConfig, UsersViewProps } from "./types";
import UsersFilters from "./components/users-filter";
import EmptyView from "@/components/ui/EmptyView";
import ButtonComponent from "@/components/ui/ButtonComponent";
import AddUserModal from "./components/add-user-modal";
import { PlusIcon } from "lucide-react";
import React from "react";
export type UserAction = "view" | "edit" | "delete" | "block" | "unblock";
export interface SelectionActionPermissions extends BaseSelectionActionPermissions {
  canEdit: boolean;
  canBlock: boolean;
  canUnblock: boolean;
}
type UsersViewProviderProps = PropsWithChildren<UsersViewProps>;
export const UsersViewContext = createContext<UsersViewConfig>(null!);
export function UsersViewProvider(t0) {
  const $ = _c(5);
  const {
    children,
    hasFilters,
    usersPromise
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <UsersSkeleton />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children || $[2] !== hasFilters || $[3] !== usersPromise) {
    t2 = <Suspense fallback={t1}><UsersViewProviderContent hasFilters={hasFilters} usersPromise={usersPromise}>{children}</UsersViewProviderContent></Suspense>;
    $[1] = children;
    $[2] = hasFilters;
    $[3] = usersPromise;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
const UsersViewProviderContent: React.FC<UsersViewProviderProps> = t0 => {
  const $ = _c(22);
  const {
    children,
    hasFilters,
    usersPromise
  } = t0;
  const {
    error,
    status,
    message,
    result: data
  } = use(usersPromise);
  const showEmptyView = data?.total === 0 && !hasFilters;
  const showFilters = Boolean(hasFilters || data?.list.length);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const selectionActions = {
      view: {
        enabled: true,
        allowMultiple: false
      },
      edit: {
        enabled: true,
        allowMultiple: false
      },
      delete: {
        enabled: true,
        allowMultiple: true
      },
      block: {
        enabled: true,
        allowMultiple: true
      },
      unblock: {
        enabled: true,
        allowMultiple: true
      }
    };
    t1 = selectedUsers => {
      const selectedCount = selectedUsers.length;
      const isActionAvailable = action => {
        const {
          enabled,
          allowMultiple
        } = selectionActions[action];
        if (!enabled) {
          return false;
        }
        if (selectedCount === 0) {
          return false;
        }
        if (selectedCount === 1) {
          return true;
        }
        return allowMultiple;
      };
      return {
        canView: isActionAvailable("view"),
        canEdit: isActionAvailable("edit"),
        canDelete: isActionAvailable("delete"),
        canBlock: isActionAvailable("block"),
        canUnblock: isActionAvailable("unblock")
      };
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const getActionPermissions = t1;
  const [isOpenModal, setIsOpenModal] = React.useState(false);
  if (error) {
    let t2;
    if ($[1] !== message || $[2] !== status) {
      t2 = <ErrorView status={status} message={message} />;
      $[1] = message;
      $[2] = status;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (showEmptyView) {
    let t2;
    if ($[4] !== data || $[5] !== showEmptyView || $[6] !== showFilters) {
      t2 = {
        tablePage: TablePages.USERS,
        data,
        showFilters,
        showEmptyView,
        getActionPermissions
      };
      $[4] = data;
      $[5] = showEmptyView;
      $[6] = showFilters;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <UsersFilters showAddUserButton={false} />;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <EmptyView><ButtonComponent onClick={() => setIsOpenModal(true)} label="buttonAddUser" icon={PlusIcon} /></EmptyView>;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== isOpenModal) {
      t5 = isOpenModal && <AddUserModal isOpen={isOpenModal} onClose={() => setIsOpenModal(false)} />;
      $[10] = isOpenModal;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== t2 || $[13] !== t5) {
      t6 = <UsersViewContext value={t2}>{t3}{t4}{t5}</UsersViewContext>;
      $[12] = t2;
      $[13] = t5;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  let t2;
  if ($[15] !== data || $[16] !== showEmptyView || $[17] !== showFilters) {
    t2 = {
      tablePage: TablePages.USERS,
      data,
      showFilters,
      showEmptyView,
      getActionPermissions
    };
    $[15] = data;
    $[16] = showEmptyView;
    $[17] = showFilters;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[19] !== children || $[20] !== t2) {
    t3 = <UsersViewContext value={t2}>{children}</UsersViewContext>;
    $[19] = children;
    $[20] = t2;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  return t3;
};
export function useUsersView() {
  const context = use(UsersViewContext);
  if (!context) {
    throw new Error("useUsersView must be used within UsersViewProvider");
  }
  return context;
}